import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "components/ui/Footer";
import ChatWindow from "./components/ChatWindow";
import ChatMessage from "./components/ChatMessage";
import ChatInput from "./components/ChatInput";
import Icon from "../../components/AppIcon";
import SystemStatus from "./components/SystemStatus";

const ContactMe = () => {
  const [messages, setMessages] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const availableSystemMessage = [
    "help",
    "send-message",
    "schedule-call",
    "view-availability",
    "download-resume",
    "clear",
    "status",
    "whoami",
    "ls",
    "ls -la",
    "pwd",
    "date",
    "uptime",
    "ping",
    "gui-mode",
    "terminal-mode",
  ];

  useEffect(() => {
    // Initialize with welcome message
    const welcomeMessages = [
      {
        type: "system",
        content: `Welcome to DeveloperNoob Contact System v2.1.0\nInitializing contact system...`,
        timestamp: Date.now() - 3000,
      },
      {
        type: "success",
        content: `✓ Email service: Online\n✓ Backend API: Connected\n✓ Resume generator: Ready\n✓ Message queue: Active`,
        timestamp: Date.now() - 2000,
      },
      {
        type: "info",
        content: `System ready! Send 'help' to see How my system can HELP you`,
        timestamp: Date.now() - 1000,
      },
    ];
    setMessages(welcomeMessages);
  }, []);

  const addMessage = (message) => {
    setMessages((prev) => [...prev, { ...message, timestamp: Date.now() }]);
  };

  const simulateTyping = (callback, delay = 1500) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      callback();
    }, delay);
  };

  const sendMessage = (content) => {
    const message = content?.toLowerCase()?.trim();

    // Add content to history
    setMessageHistory((prev) => [content, ...prev?.slice(0, 49)]);

    // Add message to output
    addMessage({
      type: "command",
      command: content,
      content: "",
      timestamp: Date.now(),
    });

    setIsProcessing(true);

    simulateTyping(() => {
      handleSendMessage(message);
      setIsProcessing(false);
    });
  };

  const handleSendMessage = (message) => {
    switch (message) {
      case "help":
        addMessage({
          type: "info",
          content:
            `Available Commands:\n\n` +
            `📧 send-message     - Start interactive message composer\n` +
            `📞 schedule-call    - Book a video/phone call\n` +
            `📅 view-availability - Check my current availability\n` +
            `📄 download-resume  - Get my latest resume\n` +
            `📊 status          - View system status\n` +
            `🖥️  gui-mode        - Switch to traditional contact form\n` +
            `🧹 clear           - Clear terminal output\n` +
            `❓ whoami          - Display contact information\n` +
            `📁 ls              - List available resources\n` +
            `🕐 date            - Show current date/time\n` +
            `⚡ uptime          - Show system uptime\n` +
            `🌐 ping            - Test connection status\n\n` +
            `Pro tip: Use ↑/↓ arrows for command history, Tab for autocomplete`,
        });
        break;

      case "send-message":
        addMessage({
          type: "system",
          content: `Initializing message composer...\nPlease provide the following information:`,
        });
        startMessageComposer();
        break;

      case "schedule-call":
        addMessage({
          type: "info",
          content:
            `📞 Schedule a Call\n\nAvailable time slots:\n` +
            `• Monday-Friday: 9:00 AM - 6:00 PM PST\n` +
            `• Video calls: Google Meet, Zoom, or Teams\n` +
            `• Phone calls: Available upon request\n\n` +
            `To book a slot, please use 'send-message' and mention:\n` +
            `- Preferred date/time\n` +
            `- Call duration (15min, 30min, 1hr)\n` +
            `- Platform preference\n` +
            `- Brief agenda/topic\n\n` +
            `I'll respond with a calendar invite within 24 hours.`,
        });
        break;

      case "view-availability":
        addMessage({
          type: "success",
          content:
            `📅 Current Availability Status\n\n` +
            `Status: Available for new projects\n` +
            `Response Time: < 24 hours\n` +
            `Next Available Call: Tomorrow 2:00 PM PST\n\n` +
            `Current Workload: 70% capacity\n` +
            `Project Queue: 2 active, 1 pending\n\n` +
            `Best times to reach me:\n` +
            `• 9:00 AM - 12:00 PM PST (Immediate response)\n` +
            `• 2:00 PM - 5:00 PM PST (Quick response)\n` +
            `• Evenings/weekends (24-48hr response)`,
        });
        break;

      case "download-resume":
        addMessage({
          type: "system",
          content:
            `Generating resume package...\n\n` +
            `📄 Available formats:\n` +
            `• PDF (Recommended) - Professional format\n` +
            `• JSON - Machine-readable developer resume\n` +
            `• HTML - Interactive web version\n` +
            `• TXT - Plain text for ATS systems\n\n` +
            `Note: This is a demo. In production, this would trigger\n` +
            `an actual download or email the resume to your address.\n\n` +
            `Use 'send-message' to request a specific format.`,
        });
        break;

      case "status":
        addMessage({
          type: "success",
          content:
            `🖥️  System Status Report\n\n` +
            `Contact System: ✅ Online\n` +
            `Email Service: ✅ Operational\n` +
            `Calendar API: ✅ Connected\n` +
            `Resume Generator: ✅ Ready\n` +
            `Message Queue: ✅ Processing\n\n` +
            `Performance Metrics:\n` +
            `• Uptime: 99.9%\n` +
            `• Avg Response Time: 18.5 hours\n` +
            `• Messages Processed: 1,247\n` +
            `• Satisfaction Rate: 98.5%\n\n` +
            `Last System Update: ${new Date()?.toLocaleDateString()}`,
        });
        break;

      case "whoami":
        addMessage({
          type: "info",
          content:
            `👨‍💻 Developer Noob - Contact Information\n\n` +
            `Name: Backend Developer → Frontend Explorer\n` +
            `Status: Available for hire\n` +
            `Location: Remote-first, PST timezone\n` +
            `Specialties: Backend systems, Learning frontend\n\n` +
            `Contact Methods:\n` +
            `• Email: Primary communication channel\n` +
            `• Video calls: Available by appointment\n` +
            `• Phone: For urgent matters only\n\n` +
            `Response Time: Usually within 24 hours\n` +
            `Best Contact Time: 9 AM - 6 PM PST, Mon-Fri`,
        });
        break;

      case "ls":
        addMessage({
          type: "info",
          content:
            `📁 Available Resources:\n\n` +
            `contact-form.html\n` +
            `resume.pdf\n` +
            `portfolio.json\n` +
            `availability.cal\n` +
            `testimonials.md\n` +
            `projects/\n` +
            `skills/\n` +
            `blog/`,
        });
        break;

      case "ls -la":
        addMessage({
          type: "info",
          content:
            `📁 Detailed Resource Listing:\n\n` +
            `drwxr-xr-x  8 dev  staff   256 Dec  6 14:52 .\n` +
            `drwxr-xr-x  5 dev  staff   160 Dec  6 14:52 ..\n` +
            `-rw-r--r--  1 dev  staff  2.1K Dec  6 14:52 contact-form.html\n` +
            `-rw-r--r--  1 dev  staff  156K Dec  6 14:52 resume.pdf\n` +
            `-rw-r--r--  1 dev  staff  8.4K Dec  6 14:52 portfolio.json\n` +
            `-rw-r--r--  1 dev  staff  1.2K Dec  6 14:52 availability.cal\n` +
            `-rw-r--r--  1 dev  staff  4.7K Dec  6 14:52 testimonials.md\n` +
            `drwxr-xr-x  12 dev staff   384 Dec  6 14:52 projects/\n` +
            `drwxr-xr-x  8 dev  staff   256 Dec  6 14:52 skills/\n` +
            `drwxr-xr-x  15 dev staff   480 Dec  6 14:52 blog/\n` +
            `-rw-r--r--  1 dev  staff   512 Dec  6 14:52 .secrets`,
        });
        break;

      case "pwd":
        addMessage({
          type: "info",
          content: `/home/developer-noob/contact-system`,
        });
        break;

      case "date":
        addMessage({
          type: "info",
          content: new Date()?.toString(),
        });
        break;

      case "uptime":
        addMessage({
          type: "success",
          content:
            `System uptime: 247 days, 18 hours, 42 minutes\n` +
            `Load average: 0.45, 0.52, 0.48\n` +
            `Users: 1 active session\n` +
            `Processes: 127 total, 3 running, 124 sleeping`,
        });
        break;

      case "ping":
        addMessage({
          type: "success",
          content:
            `PING developer-noob.contact (127.0.0.1): 56 data bytes\n` +
            `64 bytes from 127.0.0.1: icmp_seq=0 time=0.045ms\n` +
            `64 bytes from 127.0.0.1: icmp_seq=1 time=0.038ms\n` +
            `64 bytes from 127.0.0.1: icmp_seq=2 time=0.041ms\n\n` +
            `--- developer-noob.contact ping statistics ---\n` +
            `3 packets transmitted, 3 packets received, 0.0% packet loss\n` +
            `Connection: ✅ Excellent`,
        });
        break;

      case "clear":
        setMessages([]);
        break;

      // Easter eggs
      case "sudo rm -rf /":
        addMessage({
          type: "error",
          content:
            `😱 WHOA THERE!\n\nNice try, but I'm not falling for that one!\n` +
            `This is a contact form, not a production server.\n\n` +
            `Though I appreciate the classic sysadmin humor. 😄\n` +
            `Maybe we should talk - you seem like my kind of person!`,
        });
        break;

      case "exit":
      case "quit":
        addMessage({
          type: "warning",
          content:
            `You can't escape that easily! 😉\n\n` +
            `This is a contact form, not a terminal session.\n` +
            `But if you really want to leave, just close the browser tab.\n\n` +
            `Or better yet, send me a message first!`,
        });
        break;

      default:
        addMessage({
          type: "error",
          content:
            `Command not found: ${message}\n\n` +
            `Did you mean one of these?\n` +
            `• help - Show available commands\n` +
            `• send-message - Start composing a message\n` +
            `• gui-mode - Switch to traditional form\n\n` +
            `System’s up! Need a hand? Just type help.`,
        });
    }
  };

  const startMessageComposer = () => {
    // Simulate interactive message composer
    setTimeout(() => {
      addMessage({
        type: "info",
        content:
          `📝 Message Composer\n\n` +
          `For the full interactive experience, please use 'gui-mode'\n` +
          `or send your message in the following format:\n\n` +
          `Name: Your Full Name\n` +
          `Email: your.email@example.com\n` +
          `Subject: Brief description\n` +
          `Message: Your detailed message here\n\n` +
          `Or simply switch to GUI mode for a user-friendly form!`,
      });
    }, 1000);
  };

  return (
    <>
      <Helmet>
        <title>Contact Console - DeveloperNoob</title>
        <meta
          name="description"
          content="Contact DeveloperNoob through Chat messages, schedule calls."
        />
        <meta
          name="keywords"
          content="contact, developer, terminal, command line, hire developer, backend developer, frontend learning"
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <Header />

        <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Icon name="Terminal" size={32} className="text-primary" />
                <h1 className="text-3xl md:text-4xl font-bold text-foreground font-inter">
                  Contact Console
                </h1>
              </div>
              <p className="text-lg text-muted-foreground font-source max-w-2xl mx-auto">
                Welcome to the DeveloperNoob{" "}
                <span className="text-primary">Chat-CLI:</span> This isn’t
                WhatsApp, but it’s close. Drop a message here and I’ll reply
                faster than your last pull request got rejected.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Chat Area */}
              <div className="lg:col-span-2">
                <ChatWindow
                  isMinimized={isMinimized}
                  onMinimize={() => setIsMinimized(!isMinimized)}
                  onMaximize={() => setIsMinimized(false)}
                  onClose={() => {
                    addMessage({
                      type: "warning",
                      content: `Terminal session cannot be closed.\nThis is a contact form, not a real terminal! 😄\n\nTry 'gui-mode' for a traditional interface.`,
                    });
                  }}
                  title="Contact Me"
                >
                  <div className=" flex flex-col min-h-[460px] space-y-4 justify-between">
                    <ChatMessage messages={messages} isTyping={isTyping} />
                    <ChatInput
                      onCommand={sendMessage}
                      messageHistory={messageHistory}
                      isProcessing={isProcessing}
                      autoComplete={availableSystemMessage}
                    />
                  </div>
                </ChatWindow>
              </div>

              {/* System Status Sidebar */}
              <div className="lg:col-span-1">
                <SystemStatus />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ContactMe;
